import React, { useState, useEffect } from 'react';
import { loadAppEmbeds, loadAppEmbed, saveAppEmbeds, resetErrors } from '../../modules/appEmbeds'
import withRouter from 'with-router'
import { bindActionCreators } from 'redux'
import { connect } from 'react-redux'

import Typography from '@mui/material/Typography';
import Divider from '@mui/material/Divider';
import Button from '@mui/material/Button';
import ItemsList from '../../components/ItemsList'
import ItemsListSkeleton from '../../components/ItemsListSkeleton'
import LoadingIndicator from '../../components/LoadingIndicator';
import EmptyView from '../../components/EmptyView';
import Dialog from '@mui/material/Dialog';
import DialogActions from '@mui/material/DialogActions';
import DialogContent from '@mui/material/DialogContent';
import DialogContentText from '@mui/material/DialogContentText';
import DialogTitle from '@mui/material/DialogTitle';
import OptionsMenu from '../../components/OptionsMenu';
import NewFlexItemList from '../../components/NewFlexItemList';
import cloneDeep from 'lodash/cloneDeep';
import Collapse from '@mui/material/Collapse';
import { Alert, AlertTitle } from '@mui/material';
import { Trans, withTranslation, useTranslation } from 'react-i18next';


import ExportImportDialogs from '../../components/ExportImportDialogs';
import { _clearUnchangedFields, _getValidationPropsFromValidations } from '../../helper';
import API from '../../api/api';
import { FilterOptionsSelect, FilterOptionsQuery, FilterOptionsSort } from '../../components/ItemsListFilterOptions';
import UpdateAppPermissionsDialog from '../../components/UpdateAppPermissionsDialog';

class AppEmbeds extends React.Component {

  constructor(props) {
    super(props);

    this.state = {
      appEmbeds: [],
      errorLockCollapse: false,
      initialItem: null
    }
  }

  // The route carries the slug the reducer builds from the app block, the API expects the
  // gid - so it has to come out of the list response, which is the only place that pairs them
  _findGidInResponse = (response, id) => {
    if(!response || response.success !== true || !response.result) return null;
    const match = Object.values(response.result)
      .map(entry => entry[0])
      .find(appEmbed => appEmbed && `${appEmbed.app_name}-${appEmbed.block_name}` === id);
    return match ? match.gid : null;
  }

  // Loads the deeplinked app embed once the list it has to be resolved against is in
  _loadDeeplinkedAppEmbed = (response, id) => {
    if(!id) return;
    const gid = this._findGidInResponse(response, id);
    if(!gid) return;

    this.setState({ initialItem: gid });
    // Deferred by a tick on purpose. loadAppEmbeds calls this straight after dispatching its
    // response, so React batches both into one update: load_appembeds_pending goes false
    // while load_appembed_pending goes true, isPending never dips, and ItemsList - which only
    // takes new data on that edge - keeps the fields of the language before. It then treats
    // them as unsaved edits and writes them back over the incoming ones.
    setTimeout(() => this.props.loadAppEmbed(gid, this.props.editLanguage, this.props.editTheme), 0);
  }

  componentDidMount() {
    this.props.loadAppEmbeds(this.props.editTheme, true, (response) => {
      let uriParts = this.props.location.pathname.split('/');
      let id = uriParts[3] ? uriParts[3] : uriParts[2];
      this._loadDeeplinkedAppEmbed(response, id);
    });
  }

  componentDidUpdate(prevProps) {
    if (this.props.editTheme !== prevProps.editTheme) {
      // the embeds of the previous theme say nothing about the new one, so the deeplink
      // waits for the fresh list
      this._reloadForContextSwitch();
      return;
    }

    // Same treatment for a language switch as every other section gives it: reload the
    // list. Refetching only the opened embeds would leave their fields in place, and
    // ItemsList takes populated fields on a finished load for unsaved edits and keeps the
    // values that are already on screen - the switch would show the old language.
    if (this.props.editLanguage !== prevProps.editLanguage) {
      this._reloadForContextSwitch();
    }
  }

  // Back to the mount state: with the keyed ItemsList the editor unmounts, so a response
  // still out for the old context (a per-field translation above all) dies with it
  _reloadForContextSwitch = () => {
    this.setState({ appEmbeds: [], initialItem: null });
    this.props.loadAppEmbeds(this.props.editTheme, true, (response) => {
      let uriParts = this.props.location.pathname.split('/');
      let id = uriParts[3] ? uriParts[3] : uriParts[2];
      this._loadDeeplinkedAppEmbed(response, id);
    });
  }

  componentWillReceiveProps(newProps) {
    const { t } = this.props;

    if((this.props.appEmbeds.load_appembed_pending === true && newProps.appEmbeds.load_appembed_pending === false) 
    || (this.props.appEmbeds.save_appembeds_pending === true && newProps.appEmbeds.save_appembeds_pending === false) 
    || (this.props.appEmbeds.load_appembeds_pending === true && newProps.appEmbeds.load_appembeds_pending === false)) {
      const newAppEmbeds = [];
      if(newProps.appembeds && newProps.appembeds.length > 0) {
        
        newProps.appembeds.forEach(item => {
          let newFields = [];
          if(item.translations) {
            item.translations.forEach((field) => {
              newFields.push({
                id: field.key,
                type: 'text',
                label: field.key,
                source: field.source,
                target: field.target === null ? '' : field.target,
                error: field.error, 
                restore: field.restore,
              })
            })            
          }
          newAppEmbeds.push({
            type: 'appEmbed',
            id: item.id,
            gid: item.gid,
            progress: !item.progress ? -1 : item.progress,
            synchronized: item.synchronized,
            title: item.title ? item.title : '',
            info: '',
            hasError: item.hasError,
            fields: newFields ? cloneDeep(newFields) : null
          })
        })       
        this.setState({appEmbeds: newAppEmbeds});
      } else {
        // An empty list is an answer too - a theme without app blocks, or one that could not
        // be scanned. Without this the entries of the theme before stay on screen, fields and
        // translations included, and the empty state below never gets its turn
        this.setState({appEmbeds: []});
      }
    }
  }

  onExpand(id) {
    let targetItem = this.state.appEmbeds.find(x => x.id === id);
    if(targetItem && targetItem.synchronized !== true) {
      this.props.loadAppEmbed(targetItem.gid, this.props.editLanguage, this.props.editTheme);
    }
  }

  onCollapsed = (id) => {}

  onSave = items => {
    const usedItems = [];
    items.forEach(item => {
      const newTranslations = [];
      item.fields.forEach((field, i) => {
        newTranslations.push({
          key: field.id,
          value: field.target || '',
        })
      })
      usedItems.push({
        resource_id: item.gid,
        translations: newTranslations
      });
    })
    this.props.saveAppEmbeds(this.props.editLanguage, this.props.editTheme, usedItems, res => {})
  }

  // A theme that was deleted in Shopify is gone from the list as well, so the fallback
  // covers exactly the case where no name can be given
  _editThemeLabel = () => {
    const { t, themes, editTheme } = this.props;
    const theme = themes.themes.find(x => x.id == editTheme);
    return theme && theme.name ? theme.name : t('pages.appEmbeds.thisTheme');
  }

  render() {
    const { t } = this.props;

    return <React.Fragment>
      <Typography variant="h4" gutterBottom component="h2">
        {t('pages.appEmbeds.title')} - {this._editThemeLabel()}
      </Typography>

      <Divider className="divider-white" />

      <Alert severity="info">
        {t('pages.appEmbeds.sectionInfo')}
      </Alert>

      {this.props.appEmbeds.permissionUpdateNeeded  &&
        <UpdateAppPermissionsDialog shop={this.props.shop} />
      }

      <ItemsList
        type="appEmbeds"
        // fresh instance per context, as on the shop page - nothing carries over a switch
        key={`${this.props.editLanguage}-${this.props.editTheme}`}
        //useLazyloading
        //loadMoreButton={this.props.appEmbeds.cursor}
        //onLoadMore={this.onLoadMore}
        //filterModules={null}
        pagination={false}
        paginate={false}
        onExpand={(id) => {this.onExpand(id)}}
        onCollapsed={this.onCollapsed}
        isPending={
          this.props.appEmbeds.load_appembeds_pending ||
          this.props.appEmbeds.load_appembed_pending ||
          this.props.appEmbeds.save_appembeds_pending
        }
        isSaving={
          this.props.appEmbeds.save_appembeds_pending
        }
        onSave={this.onSave}
        data={this.state.appEmbeds}
        allowAutoTranslation={true}
        error={this.props.appEmbeds.error}
        errorLockCollapse={this.state.errorLockCollapse}
        saveErrors={this.props.appEmbeds.saveErrors}
        emptyMessage={
          this.props.appEmbeds.appembeds_unavailable ?
          // the app store button would be wrong here: installing an app fixes neither an
          // unreadable theme nor a deleted one
          <EmptyView
            title={t('pages.appEmbeds.themeUnavailableTitle')}
            description={
              // Trans instead of t(), because the theme name is set off in bold - and unlike
              // the dangerouslySetInnerHTML the older keys use, no markup reaches the DOM
              <Trans
                i18nKey="pages.appEmbeds.themeUnavailableDescription"
                values={{theme: this._editThemeLabel()}}
              />
            }
          />
        :
          (this.props.appEmbeds.load_appembeds_pending === false && this.props.appEmbeds.appembeds.length === 0) ? 
          <EmptyView 
            title={t('pages.appEmbeds.noContent')}
            bottom={<Button href={`https://apps.shopify.com/`} target="_blank" variant="contained" color="primary">{t('system.addOneHere')}</Button>}
          />
        :
          <ItemsListSkeleton rows={5} />
        }
      />
      <LoadingIndicator
        pending={
          (this.props.appEmbeds.load_appembeds_pending && this.state.appEmbeds.length === 0) || 
          this.props.appEmbeds.load_appembed_pending || 
          this.props.appEmbeds.save_appembeds_pending
        }
        loadingType={this.props.appEmbeds.save_appembeds_pending ? 'saving' : 'loading'}
        progress={(this.props.appEmbeds.progress < 0) ? 0 : this.props.appEmbeds.progress}
        error={this.props.appEmbeds.error}
        onErrorCancel={this.props.resetErrors}
        onErrorRetry={() => {
          this.setState({ errorLockCollapse: true }, () => {
            this.setState({ errorLockCollapse: false });
          });
        }}
      />
    </React.Fragment> 
  }
}


const mapStateToProps = ({ appEmbeds, shop, themes }) => ({
  appEmbeds: appEmbeds,
  appembeds: appEmbeds.appembeds,
  pageInfo: appEmbeds.page_info,
  editLanguage: shop.shop.edit_language,
  themes: themes,
  editTheme: themes.edit_theme,
  shopUrl: shop.shop.url,
  shopPlan: shop.shop.charge.name,
  shop: shop.shop
})

const mapDispatchToProps = dispatch =>
  bindActionCreators(
    {
      loadAppEmbeds,
      loadAppEmbed,
      saveAppEmbeds,
      resetErrors
    },
    dispatch
  )

export default withRouter(connect(
  mapStateToProps,
  mapDispatchToProps
)(withTranslation()(AppEmbeds)))
